<template lang="pug">
div
  v-row.my-4(no-gutters v-if="!showEditMode")
    v-col(cols=8)
      .text-lg.font-semibold.mb-1 {{ transaction.payment_method }}
      .text-xs(v-if="transaction.internal_description") Payment notes: {{ transaction.internal_description }}
      template(v-if="transaction.documents.length > 0")
        .text-xs.mb-1 Uploaded photos:
        .d-flex
          div(v-for="photo in transaction.documents")
            v-img.round-md.clickable.mr-2(width="30" height="30" :src="$options.filters.smallImage(photo.file)" cover @click="viewImage(photo.file)")
              template(v-slot:placeholder)
                v-row(no-gutters class="fill-height ma-0" align="center" justify="center")
                  v-progress-circular(indeterminate size="15" color="grey" width="2")
    v-col.text-right
      .text-lg.font-semibold {{ transaction.amount | displayPrice }}
      .text-xs.font-semibold.underline.mt-1(v-if="transaction.channel === 'panel'")
        span.clickable(@click="showVerifyPanelDialog = true") {{ $t('verify_panel_info') }}
      .text-xs.font-semibold.underline.mt-1(v-if="transaction.channel === 'self-pay'")
        span.clickable(@click="onDownloadReceipt") {{ $t('print_receipt') }}
      .text-xs.font-semibold.underline.mt-1
        span.clickable(@click="editTransaction") {{ $t('edit') }}
      .text-xs.font-semibold.underline.mt-1.danger500--text(v-if="transaction.channel === 'panel' && !transaction.paid")
        span.clickable(@click="openUpdateClaimDialog = true") Update claim status

  template(v-else)
    v-row.mt-4(dense)
      v-col(cols=3)
        y-select(v-model="paymentType" placeholder="Panel" :items="paymentTypes" item-text="name" item-value="id" attach)
      v-col(cols=5)
        y-select(v-if="paymentType === 'panel'" v-model="selectedPanel" placeholder="Choose panel" :items="panels" item-text="name" item-value="id" attach)
        y-select(v-else v-model="selectedSelfPayMethod" placeholder="Choose payment method" :items="selfPayMethods" item-text="name" item-value="id" attach)
      v-col(cols=4)
        v-text-field.text-sm.round-xl(outlined dense height="48" hide-details v-model="amount" :prefix="$currency.symbol" type="number")

    .d-flex.my-3
      .flex-grow-1
        .d-flex
          .text-xs.mr-2(v-if="note") Payment notes: {{ note }}
          .text-xs.font-semibold.underline.clickable.mr-4(@click="showPaymentNoteDialog = true") {{ note ? 'Edit' : 'Add payment notes' }}
          .text-xs.font-semibold.underline.clickable(v-if="transaction.documents.length === 0" @click="showUploadPhotoDialog = true") Upload photo
        template(v-if="transaction.documents.length > 0")
          .d-flex.my-1
            .text-xs.mr-2(v-if="note") Uploaded photos:
            .text-xs.font-semibold.underline.clickable.mr-4(@click="showUploadPhotoDialog = true") Edit
          .d-flex(v-if="transaction.documents.length > 0")
            div(v-for="photo in transaction.documents")
              v-img.round-md.clickable.mr-2(width="30" height="30" :src="$options.filters.smallImage(photo.file)" cover @click="viewImage(photo.file)")
                template(v-slot:placeholder)
                  v-row(no-gutters class="fill-height ma-0" align="center" justify="center")
                    v-progress-circular(indeterminate size="15" color="grey" width="2")
                    
      .text-xs.font-semibold.underline.clickable.danger--text(@click="showConfirmationDialog = true") Delete this payment

    .d-flex.align-center.mb-4.mt-6
      .text-xs.font-semibold.underline.clickable(@click="showEditMode = false") {{ $t('cancel') }}
      v-spacer
      y-button.mr-2(v-if="paymentType === 'panel'" label="Verify panel" outlined size="sm" @click="showVerifyPanelDialog = true")
      y-button(label="Apply changes" size="sm" @click="applyChanges" :loading="isLoading" :disabled="disabledPatchButton")
    
  v-divider

  payment-note-dialog(v-if="showPaymentNoteDialog" :open.sync="showPaymentNoteDialog" :note="note" @onSaved="updateNote")
  verify-panel-dialog(v-if="showVerifyPanelDialog" :open.sync="showVerifyPanelDialog" :selectedPanel="transaction.ref_id")
  upload-photo-dialog(v-if="showUploadPhotoDialog" :open.sync="showUploadPhotoDialog" :transaction="transaction" @updated="updateTransaction")
  update-claim-dialog(v-if="openUpdateClaimDialog" :open.sync="openUpdateClaimDialog" :selected-bills="selectedBills" @updated="updateTransaction" @created="onClaimDialogCreated" @reset="onUpdateClaimDialogReset" @closed="openUpdateClaimDialog = false")
  send-claim-dialog(v-if="showSendClaimDialog" v-model="showSendClaimDialog" :claim-id="createdClaimId" @close="onSendClaimDialogClosed")
  view-image-dialog(v-if="showViewImageDialog" :open.sync="showViewImageDialog" :image="selectedImage" @closed="selectedImage = null")

  y-dialog(v-if="showConfirmationDialog" v-model="showConfirmationDialog" max-width=480 persistent fluid scrollable content-class="round-2xl")
    v-card
      v-card-text.pa-8.primary--text
        template(v-if="isDeleteRestricted")
          .d-flex.align-center
            v-icon.mr-2(color="danger500" size="18") $yzi-alert-circle
            .text-md.font-medium Unable to delete payment
          .text-sm.mt-2(v-if="isClaimReceived") This payment has a linked panel claim that has been received and cannot be deleted.
          .text-sm.mt-2(v-else) This payment has a linked panel claim and cannot be deleted. Please revert the claim to invoice first before deleting this payment.
          yz-card.mt-4.pa-3(outlined color="yz-neutral-200" style="border-radius: 10px;")
            .d-flex(style="gap: 12px;")
              .yz-medium-body-sm.text-neutral-500(style="width: 160px;") Claim number
              .d-flex.align-center(style="gap: 8px;")
                .yz-medium-body-sm.text-neutral-700 Claim {{ `#${transaction.panel_claim.rid}` }}
                claim-status-badge(:status="transaction.panel_claim.status")
            .d-flex.mt-3(style="gap: 12px;")
              .yz-medium-body-sm.text-neutral-500(style="width: 160px;") Payment amount
              .yz-medium-body-sm.text-neutral-700 {{ transaction.amount | displayPrice }}
        template(v-else)
          .text-md.font-medium.mb-4 Delete this payment?
          .text-sm {{ $t('click_confirm_to_proceed') }}
      v-divider
      v-card-actions.px-6.py-3
        v-spacer
        template(v-if="isDeleteRestricted")
          y-button(@click="showConfirmationDialog = false" size="sm" label="Close")
        template(v-else)
          y-button(@click="showConfirmationDialog = false" size="sm" :label="$t('cancel')" underline)
          y-button(@click="deleteTransaction" :loading="isDeleting" color="danger" :label="$t('confirm')" size="sm" dark)
  download-invoice-receipt-dialog(v-if="openSelectReceiptDialog" :open.sync="openSelectReceiptDialog" :documents="receiptTemplates" documentType="receipt" :transaction="transaction")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import PaymentNoteDialog from '@/components/clinic/dialogs/PaymentNoteDialog.vue'
import VerifyPanelDialog from '@/components/clinic/dialogs/VerifyPanelDialog.vue'
import UploadPhotoDialog from '@/components/clinic/dialogs/UploadPhotoDialog.vue'
import DownloadInvoiceReceiptDialog from '@/components/clinic/dialogs/DownloadInvoiceReceiptDialog.vue'
import UpdateClaimDialog from '@/components/clinic/dialogs/UpdateClaimDialog.vue'
import ViewImageDialog from '@/components/dialogs/ViewImageDialog.vue'
import SendClaimDialog from '@/components/clinic/panel/dialogs/SendClaimDialog.vue'
import ClaimStatusBadge from '@/components/clinic/panel/claims/ClaimStatusBadge.vue'

export default {
  name: 'PaymentItem',
  components: { PaymentNoteDialog, VerifyPanelDialog, UploadPhotoDialog, DownloadInvoiceReceiptDialog, UpdateClaimDialog, ViewImageDialog, SendClaimDialog, ClaimStatusBadge },
  props: {
    transaction: {
      type: Object,
      required: true
    },
    panels: {
      type: Array,
      required: true,
      default: () => []
    },
    selfPayMethods: {
      type: Array,
      required: true,
      default: () => []
    },
    invoice: {
      default: null
    }
  },
  data: () => ({
    note: null,
    paymentType: null,
    paymentTypes: [
      { id: 'self-pay', name: 'Self-pay' },
      { id: 'panel', name: 'Panel' },
    ],
    selectedPanel: null,
    selectedSelfPayMethod: null,
    amount: null,
    selectedImage: null,
    isLoading: false,
    isDeleting: false,
    showEditMode: false,
    showPaymentNoteDialog: false,
    showVerifyPanelDialog: false,
    showConfirmationDialog: false,
    showUploadPhotoDialog: false,
    showViewImageDialog: false,
    openUpdateClaimDialog: false,
    isLoadingDownloadReceipt: false,
    openSelectReceiptDialog: false,
    showSendClaimDialog: false,
    createdClaimData: null,
    receiptTemplates: []
  }),
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    selectedPayment () {
      return this.paymentType === 'panel' ? this.panels.find(i => i.id === this.selectedPanel) : this.selfPayMethods.find(i => i.id === this.selectedSelfPayMethod)
    },
    disabledPatchButton () {
      return !this.amount || (this.paymentType === 'self-pay' && !this.selectedSelfPayMethod) || (this.paymentType === 'panel' && !this.selectedPanel)
    },
    leadPid() {
      return this.$route.params.pid
    },
    fetchReceiptParams () {
      return {
        company: this.company.id,
        categories: 'receipt'
      }
    },
    selectedBills () {
      let bills = null
      if (this.invoice) {
        bills = [{
          pid: this.leadPid,
          rid: this.invoice.rid,
          record_date: this.transaction.created,
          lead: this.invoice.customer,
          products: this.invoice.products,
          transactions: [this.transaction]
        }]
      }
      return bills
    },
    createdClaimId () {
      return this.createdClaimData?.id ?? null
    },
    isClaimReceived () {
      return this.transaction.panel_claim?.status === 'received'
    },
    isDeleteRestricted () {
      return !!this.transaction.panel_claim
    }
  },
  mounted () {
    this.paymentType = this.paymentTypes[0].id
  },
  methods: {
    ...mapActions(['showMessage']),
    updateNote (note) {
      this.note = note
    },
    editTransaction () {
      this.showEditMode = true
      this.paymentType = this.transaction.channel
      if (this.paymentType === 'panel') {
        this.selectedPanel = Number(this.transaction.ref_id)
      } else {
        this.selectedSelfPayMethod = Number(this.transaction.ref_id)
      }
      this.note = this.transaction.internal_description
      this.amount = this.transaction.amount / 100
    },
    deleteTransaction () {
      this.isDeleting = true
      const endpoint = `/transactions/${this.transaction.uid}`
      this.axios.delete(endpoint)
        .then(() => {
          let activity = `Delete payment (#${this.invoice.rid})`
          this.updateOldActivityLog(endpoint, null, null, 'DELETE', this.transaction.uid, activity)
          const amount = this.$options.filters.displayPrice(this.transaction.amount, this.$currency)
          this.createActivityLog('delete_transaction', `${this.transaction.payment_method} amount ${amount} was removed`)
          this.showConfirmationDialog = false
          this.showEditMode = false
          this.$emit('deleted')
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    applyChanges () {
      this.isLoading = true
      const amountInCent = (parseFloat(this.amount)).toFixed(this.$currency.exponent).replace('.', '')
      const payload = {
        amount: amountInCent,
        channel: this.paymentType,
        ref_id: this.selectedPayment?.id,
        payment_method: this.selectedPayment?.name,
        internal_description: this.note
      }
      const endpoint = this.isHq ? `/hq/transactions/${this.transaction.uid}` : `/transactions/${this.transaction.uid}`
      this.axios.patch(endpoint, payload)
        .then(resp => {
          let activity = `Edit payment (#${this.invoice.rid})`
          this.updateOldActivityLog(endpoint, payload, resp.data, 'PATCH', this.transaction.uid, activity)
          const amount = this.$options.filters.displayPrice(resp.data.amount, this.$currency)
          this.createActivityLog('edit_transaction', `${resp.data.payment_method} was edited to amount ${amount}`)
          this.showEditMode = false
          this.$emit('updated', resp.data)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async onDownloadReceipt () {
      try {
        this.isLoadingDownloadReceipt = true
        const { data } = await this.$axios.get('/document_templates', { params: this.fetchReceiptParams })
        if (data.count > 1) {
          this.receiptTemplates = data.results
          this.openSelectReceiptDialog = true
          this.isLoadingDownloadReceipt = false
          return
        }
        if (data.count === 1) {
          this.onPrint(data.results[0])
          return
        }
        this.showMessage(['error', this.$t('error_message.create_item_first', { item: this.$t('receipt').toLowerCase() })])
        if (this.isHq) {
          return
        }
        this.$router.push('/admin/clinic/settings/document/templates')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDownloadReceipt = false
      }
    },
    async onPrint (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingDownloadReceipt = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/invoices/${this.leadPid}/preview_document?document_template=${item.id}&transaction=${this.transaction.uid}&key=${key}`
      this.isLoadingDownloadReceipt = false
      window.open(url,'_blank')
    },
    viewImage (image) {
      this.selectedImage = image
      this.showViewImageDialog = true
    },
    onClaimDialogCreated (claimData) {
      this.createdClaimData = claimData
      this.openUpdateClaimDialog = false
      this.showSendClaimDialog = true
    },
    async onSendClaimDialogClosed () {
      this.createdClaimData = null
      try {
        const freshTransaction = await this.fetchTransaction()
        this.updateTransaction(freshTransaction)
      } catch {
        this.updateTransaction()
      }
    },
    async onUpdateClaimDialogReset () {
      try {
        const freshTransaction = await this.fetchTransaction()
        this.updateTransaction(freshTransaction)
      } catch {
        this.updateTransaction()
      }
      this.openUpdateClaimDialog = false
    },
    async fetchTransaction () {
      const { data } = await this.$axios.get(`/transactions/${this.transaction.uid}`)
      return data
    },
    updateTransaction (response) {
      this.$emit('updated', response)
    },
    updateOldActivityLog (endpoint, payload, response, method, id, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'checkout',
        activity: activity,
        raw_data: { endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    }
  }
}
</script>